/* assets/logo-marquee-pro.js */
(() => {
  const ROOT_SEL = "[data-lm]";
  const VIEWPORT_SEL = "[data-lm-viewport]";
  const TRACK_SEL = "[data-lm-track]";
  const SEQ_SEL = "[data-lm-seq]";

  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

  class LogoMarqueePro {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(VIEWPORT_SEL);
      this.track = root.querySelector(TRACK_SEL);
      this.seq = root.querySelector(SEQ_SEL);

      this._raf = 0;
      this._ro = null;
      this._io = null;
      this._mm = window.matchMedia("(min-width: 990px)");

      if (!this.viewport || !this.track || !this.seq) return;
      if (this.root.dataset.lmInit === "1") return;

      this.root.dataset.lmInit = "1";

      this.onMedia = () => this.schedule();
      if (this._mm && this._mm.addEventListener) this._mm.addEventListener("change", this.onMedia);
      else if (this._mm && this._mm.addListener) this._mm.addListener(this.onMedia);

      this.onVis = () => {
        if (document.hidden) this.pause(true);
        else this.pause(false);
      };
      document.addEventListener("visibilitychange", this.onVis, { passive: true });

      this.setupObservers();

      // 로딩 안정화 후 1회 측정 → 이후 RO/IO로 재측정
      this.waitForAssets().then(() => this.schedule());
    }

    getSpeed() {
      const isD = this._mm && this._mm.matches;
      const s = isD ? Number(this.root.dataset.speedD) : Number(this.root.dataset.speedM);
      // px/sec. 너무 낮으면 duration이 과도하게 커질 수 있어 하한 보장.
      return clamp(isFinite(s) ? s : 120, 40, 260);
    }

    pause(v) {
      this.root.classList.toggle("is-paused", !!v);
    }

    setupObservers() {
      // ResizeObserver: 레이아웃 변화(뷰포트 폭/폰트 로딩/에디터 변경) 대응
      if ("ResizeObserver" in window) {
        this._ro = new ResizeObserver(() => this.schedule());
        this._ro.observe(this.viewport);
        this._ro.observe(this.seq);
      } else {
        window.addEventListener("resize", () => this.schedule(), { passive: true });
      }

      // IntersectionObserver: 화면 밖이면 pause (불필요한 애니메이션/CPU 줄이기)
      if ("IntersectionObserver" in window) {
        this._io = new IntersectionObserver(
          (entries) => {
            const e = entries && entries[0];
            if (!e) return;
            this.pause(!e.isIntersecting);
          },
          { root: null, threshold: 0.01 }
        );
        this._io.observe(this.root);
      }
    }

    async waitForAssets() {
      const imgs = Array.from(this.root.querySelectorAll("img"));

      const waitOne = (img) => {
        if (!img) return Promise.resolve();
        if (img.complete && img.naturalWidth > 0) {
          if (img.decode) return img.decode().catch(() => {});
          return Promise.resolve();
        }
        return new Promise((res) => {
          const done = () => res();
          img.addEventListener("load", done, { once: true, passive: true });
          img.addEventListener("error", done, { once: true, passive: true });
        });
      };

      await Promise.allSettled(imgs.map(waitOne));

      // 폰트 로딩 후 폭 변화도 반영
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (_) {}
      }
    }

    schedule() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(() => this.recalc());
    }

    clearClones() {
      const clones = this.track.querySelectorAll(".lm__seq.is-clone");
      clones.forEach((n) => n.remove());
    }

    recalc() {
      if (!this.seq || !this.viewport || !this.track) return;

      // 초기화 (누적 복제/잘못된 distance로 인한 느려짐 방지)
      this.root.classList.remove("is-ready");
      this.clearClones();

      // 애니메이션 강제 리셋 (브라우저 캐시/타이밍 이슈 방지)
      this.track.style.animation = "none";
      // eslint-disable-next-line no-unused-expressions
      this.track.offsetHeight;
      this.track.style.animation = "";

      const speed = this.getSpeed();
      const viewW = Math.max(1, Math.ceil(this.viewport.getBoundingClientRect().width));

      // 원본 시퀀스 폭 (seam 유지를 위해 seq padding-right 포함)
      const distance = Math.max(1, Math.ceil(this.seq.getBoundingClientRect().width));

      // 로고가 적을 때도 빈 구간 없이: viewport 2배 이상 채워질 때까지 복제
      const target = viewW * 2 + distance;
      let current = distance;
      let guard = 0;

      while (current < target && guard < 24) {
        const clone = this.seq.cloneNode(true);
        clone.classList.add("is-clone");
        clone.setAttribute("aria-hidden", "true");

        // 포커스가 무한히 이어지는 문제 방지
        clone.querySelectorAll("a").forEach((a) => a.setAttribute("tabindex", "-1"));

        this.track.appendChild(clone);
        current += distance;
        guard += 1;
      }

      // duration = distance / speed(px/sec) → 새로고침 시 “갑자기 느려짐” 구조적으로 방지
      let dur = distance / speed;
      dur = clamp(dur, 2, 240);

      this.root.style.setProperty("--lm-distance", `${distance}px`);
      this.root.style.setProperty("--lm-duration", `${dur.toFixed(3)}s`);

      this.root.classList.add("is-ready");
    }

    destroy() {
      if (this._raf) cancelAnimationFrame(this._raf);

      document.removeEventListener("visibilitychange", this.onVis);

      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();

      if (this._mm && this._mm.removeEventListener) this._mm.removeEventListener("change", this.onMedia);
      else if (this._mm && this._mm.removeListener) this._mm.removeListener(this.onMedia);

      this.clearClones();
      this.root.classList.remove("is-ready", "is-paused");
      delete this.root.dataset.lmInit;
    }
  }

  const INST = new WeakMap();

  const initWithin = (root = document) => {
    root.querySelectorAll(ROOT_SEL).forEach((el) => {
      if (el.dataset.lmInit === "1") return;
      const inst = new LogoMarqueePro(el);
      INST.set(el, inst);
    });
  };

  const destroyWithin = (root) => {
    root.querySelectorAll(ROOT_SEL).forEach((el) => {
      const inst = INST.get(el);
      if (inst) inst.destroy();
      INST.delete(el);
    });
  };

  // First load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  // Theme editor support
  document.addEventListener("shopify:section:load", (e) => initWithin(e.target));
  document.addEventListener("shopify:section:unload", (e) => destroyWithin(e.target));
})();
